/**
 * ACCÈS AUX COMPTES — LES PAGES PUBLIQUES (lot INV, v83 ; maquette « invitation et mot de passe » rév. 1, écrans ⑦ ⑧ ⑨)
 * ======================================================================================================================
 *   · `MotDePasseOublie` (/mot-de-passe-oublie) : la demande, puis la MÊME réponse dans tous les cas ;
 *   · `MotDePasseLien` (/mot-de-passe/:jeton) : « Créer mon mot de passe » (invitation) ou « Choisir un nouveau mot de
 *     passe » (réinitialisation) ; lien non valable → « Lien expiré », sans aucune donnée du compte.
 *
 * Même habit que la page de connexion (fond bleu, carte blanche : `.login-page`, `.login-card`). Pages hors session,
 * rendues quel que soit l'état de connexion (App.js, `routesPubliques`).
 * ⚠️ L'indicateur de robustesse est INDICATIF et calculé ici (aucune bibliothèque) ; les règles qui comptent sont
 * vérifiées par le serveur (longueur du réglage, 72 octets, ni nom ni adresse).
 */
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { comptesPublicAPI, brandingAPI } from '../services/api';
import Footer from './Footer';
import './MotDePasse.css';

/** Décision du 2026-10-18 (point 3) : sur la version de test, la page le dit — on ne change pas son vrai mot de passe ici. */
function BandeauTest({ vue }) {
    if (!vue?.version_test) return null;
    return (
        <div className="alert mdp-alerte mdp-alerte-test"><i className="fa-solid fa-flask" />
            <div><b>Version de test</b>{vue.site ? ` (${vue.site})` : ''} : ce mot de passe ne vaut que pour la version de test. Votre mot de passe de la version normale ne change pas.</div></div>
    );
}

function Habit({ icone, titre, children }) {
    const [nom, setNom] = useState('DÉPARTEMENT MANAGER');
    useEffect(() => { brandingAPI.get().then((r) => r.data?.nom_application && setNom(r.data.nom_application)).catch(() => {}); }, []);
    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-card mdp-carte">
                    <div className="mdp-appli"><i className="fa-solid fa-building-columns" /> {nom}</div>
                    <h1><i className={'fa-solid ' + icone} /> {titre}</h1>
                    {children}
                </div>
            </div>
            <Footer />
        </div>
    );
}

/** Un champ mot de passe avec « afficher / masquer ». */
export function ChampSecret({ id, valeur, onChange, autoComplete, autoFocus }) {
    const [voir, setVoir] = useState(false);
    return (
        <div className="mdp-secret">
            <input id={id} type={voir ? 'text' : 'password'} value={valeur} autoComplete={autoComplete} autoFocus={autoFocus}
                   onChange={(e) => onChange(e.target.value)} />
            <button type="button" className="mdp-oeil" onClick={() => setVoir((v) => !v)}
                    title={voir ? 'Masquer le mot de passe' : 'Afficher le mot de passe'} aria-label={voir ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}>
                <i className={'fa-solid ' + (voir ? 'fa-eye-slash' : 'fa-eye')} />
            </button>
        </div>
    );
}

// ── « Mot de passe oublié » ──────────────────────────────────────────────────────────────────────────────────
export function MotDePasseOublie() {
    const [email, setEmail] = useState('');
    const [piege, setPiege] = useState('');
    const [etat, setEtat] = useState('saisie');       // saisie | envoi | fait
    const [erreur, setErreur] = useState(null);

    const envoyer = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setEtat('envoi'); setErreur(null);
        try { await comptesPublicAPI.demanderLien(email.trim(), piege); setEtat('fait'); }
        catch (x) {
            setEtat('saisie');
            setErreur(x.response?.status === 429 ? 'Trop de demandes depuis cette adresse : réessayez dans quelques minutes.' : 'Le service ne répond pas. Réessayez plus tard.');
        }
    };

    return (
        <Habit icone="fa-key" titre="Mot de passe oublié">
            {etat === 'fait' ? (<>
                <div className="alert alert-success mdp-alerte"><i className="fa-solid fa-envelope-circle-check" />
                    <div><b>Si un compte existe pour cette adresse</b>, un courriel vient de partir avec un lien valable <b>1 heure</b>. Pensez à regarder dans les indésirables.</div></div>
                <p className="mdp-texte">Rien reçu dans quelques minutes ? Votre compte se connecte peut-être par l'ENT, ou votre adresse est différente : demandez à l'administrateur de votre département.</p>
                <Link className="mdp-lien" to="/">Retour à la connexion</Link>
            </>) : (
                <form onSubmit={envoyer}>
                    <p className="mdp-texte">Indiquez l'adresse de votre compte : nous vous enverrons un lien pour choisir un nouveau mot de passe.</p>
                    {erreur && <div className="alert alert-error">{erreur}</div>}
                    <div className="form-group">
                        <label htmlFor="mdp-email">Email</label>
                        <input id="mdp-email" type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    {/* ⚠️ LE CHAMP PIÈGE : hors de l'écran, sauté au clavier, ignoré par les gestionnaires de mots de passe.
                        Un humain ne le voit pas ; un robot qui remplit tout le remplit — sa demande est ignorée en silence. */}
                    <div className="mdp-piege" aria-hidden="true">
                        <label htmlFor="mdp-site">Site web</label>
                        <input id="mdp-site" type="text" name="site_web" tabIndex={-1} autoComplete="off" value={piege} onChange={(e) => setPiege(e.target.value)} />
                    </div>
                    <button type="submit" className="btn btn-primary mdp-bouton" disabled={etat === 'envoi'}>
                        {etat === 'envoi' ? 'Envoi…' : 'Envoyer le lien'}
                    </button>
                    <Link className="mdp-lien" to="/">Retour à la connexion</Link>
                </form>
            )}
        </Habit>
    );
}

// ── La robustesse (indicative) et les règles ─────────────────────────────────────────────────────────────────
function robustesse(m) {
    if (!m) return { pct: 0, lib: '', couleur: '#e2e8f0' };
    let s = Math.min(m.length, 20) * 3;
    s += [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(m)).length * 10;
    if (/(.)\1\1/.test(m)) s -= 15;
    if (/^(azerty|qwerty|motdepasse|password|123456)/i.test(m)) s -= 30;
    s = Math.max(0, Math.min(100, s));
    if (s < 45) return { pct: s, lib: 'faible', couleur: '#dc2626' };
    if (s < 70) return { pct: s, lib: 'moyenne', couleur: '#f59e0b' };
    return { pct: s, lib: 'bonne', couleur: '#16a34a' };
}

function regles(m, confirmation, { longueur_min: min, prenom, nom, email }) {
    const bas = m.toLowerCase();
    const mots = [prenom, nom, String(email || '').split('@')[0]].map((x) => String(x || '').trim().toLowerCase()).filter((x) => x.length >= 3);
    return [
        { ok: m.length >= min, lib: <>au moins <b>{min}</b> caractères</> },
        { ok: !!m && !mots.some((x) => bas.includes(x)), lib: 'ni votre nom, ni votre prénom, ni votre adresse' },
        { ok: !!m && m === confirmation, lib: 'les deux saisies identiques' }
    ];
}

const dateHeure = (iso) => {
    const d = new Date(iso);
    return `${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

// ── Le lien : créer / choisir un mot de passe ────────────────────────────────────────────────────────────────
export function MotDePasseLien() {
    const { jeton } = useParams();
    const [vue, setVue] = useState(null);
    const [erreurPage, setErreurPage] = useState(null);
    const [mdp, setMdp] = useState('');
    const [conf, setConf] = useState('');
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [fait, setFait] = useState(null);

    useEffect(() => {
        comptesPublicAPI.lireLien(jeton || '')
            .then((r) => setVue(r.data))
            .catch((x) => setErreurPage(x.response?.status === 429 ? 'Trop de demandes depuis cette adresse : réessayez dans quelques minutes.' : 'Le service ne répond pas. Réessayez plus tard.'));
    }, [jeton]);

    if (erreurPage) return <Habit icone="fa-triangle-exclamation" titre="Indisponible"><div className="alert alert-error">{erreurPage}</div></Habit>;
    if (!vue) return <Habit icone="fa-circle-notch" titre="Chargement…"><p className="mdp-texte">Un instant…</p></Habit>;

    const invitation = vue.type === 'invitation';
    if (vue.etat !== 'valide' && !fait) {
        return (
            <Habit icone="fa-hourglass-end" titre="Lien expiré">
                <BandeauTest vue={vue} />
                <div className="alert mdp-alerte mdp-alerte-warn"><i className="fa-solid fa-triangle-exclamation" />
                    <div>{invitation ? <>Ce lien d'<b>invitation</b> n'est plus valable (expiré, déjà utilisé ou remplacé par un plus récent).</>
                        : vue.type === 'reinitialisation' ? <>Ce lien de <b>réinitialisation</b> n'est plus valable (1 heure, un seul usage).</>
                        : <>Ce lien n'est pas valable : il a pu être mal recopié, ou il a expiré.</>}</div></div>
                {invitation ? (<>
                    <p className="mdp-texte">Demandez une nouvelle invitation à l'administrateur de votre département. Si vous avez déjà créé votre mot de passe, connectez-vous.</p>
                    <Link className="btn btn-primary mdp-bouton" to="/">Aller à la connexion</Link>
                </>) : (<>
                    <Link className="btn btn-primary mdp-bouton" to="/mot-de-passe-oublie">Demander un nouveau lien</Link>
                    <Link className="mdp-lien" to="/">Retour à la connexion</Link>
                </>)}
            </Habit>
        );
    }

    if (fait) {
        return (
            <Habit icone="fa-user-check" titre="C'est fait">
                <BandeauTest vue={vue} />
                {fait === 'demo' && <div className="alert alert-success mdp-alerte"><i className="fa-solid fa-flask" /><div><b>Démonstration</b> : rien n'a été enregistré. Vos collègues verront cette page avec LEUR lien.</div></div>}
                {fait !== 'demo' && <div className="alert alert-success mdp-alerte"><i className="fa-solid fa-circle-check" />
                    <div>{fait === 'invitation' ? 'Votre mot de passe est créé. Vous pouvez maintenant vous connecter.'
                        : <>Votre mot de passe est changé. <b>Vos sessions ouvertes ont été fermées</b> (sur tous vos appareils).</>}</div></div>}
                {/* ⚠️ Un VRAI rechargement (<a>, pas <Link>) : si ce navigateur avait une session ouverte, elle vient d'être
                    fermée par le serveur — on l'oublie aussi ici, sinon l'application s'ouvrirait sur une session morte. */}
                <a className="btn btn-primary mdp-bouton" href="/" onClick={() => { try { localStorage.removeItem('token'); localStorage.removeItem('user'); } catch (_) { /* rien */ } }}>Aller à la connexion</a>
            </Habit>
        );
    }

    const R = regles(mdp, conf, vue);
    const F = robustesse(mdp);
    const valide = R.every((r) => r.ok);
    const enregistrer = async (e) => {
        e.preventDefault();
        if (!valide) return;
        setEnCours(true); setErreur(null);
        try { const r = await comptesPublicAPI.definirMotDePasse(jeton, mdp); setFait(r.data?.demo ? 'demo' : (r.data?.type || vue.type)); }
        catch (x) {
            if (x.response?.status === 410) setVue({ etat: 'expire', type: vue.type });
            else setErreur(x.response?.data?.error || "Le mot de passe n'a pas pu être enregistré. Réessayez.");
        } finally { setEnCours(false); }
    };

    return (
        <Habit icone="fa-user-check" titre={invitation ? `Bienvenue${vue.prenom ? ', ' + vue.prenom : ''}` : 'Choisir un nouveau mot de passe'}>
            <BandeauTest vue={vue} />
            {vue.demo && <div className="alert mdp-alerte mdp-alerte-test"><i className="fa-solid fa-eye" /><div><b>Démonstration</b> (lien de l'essai) : la page telle que vos collègues la verront ; rien ne sera enregistré.</div></div>}
            <p className="mdp-texte">{invitation ? 'Choisissez le mot de passe de votre compte.' : 'Choisissez le nouveau mot de passe de votre compte.'}</p>
            {erreur && <div className="alert alert-error">{erreur}</div>}
            <form onSubmit={enregistrer}>
                {/* l'adresse, pour les gestionnaires de mots de passe du navigateur (autoComplete « username ») */}
                <div className="form-group"><label htmlFor="mdp-compte">Email</label>
                    <input id="mdp-compte" type="email" value={vue.email} readOnly autoComplete="username" /></div>
                <div className="form-group"><label htmlFor="mdp-1">Mot de passe</label>
                    <ChampSecret id="mdp-1" valeur={mdp} onChange={setMdp} autoComplete="new-password" autoFocus />
                    <div className="mdp-force"><div style={{ width: F.pct + '%', background: F.couleur }} /></div>
                    {F.lib && <div className="mdp-force-lib">Robustesse : <b style={{ color: F.couleur }}>{F.lib}</b></div>}
                </div>
                <ul className="mdp-regles">
                    {R.map((r, i) => <li key={i} className={r.ok ? 'ok' : ''}><i className={'fa-solid ' + (r.ok ? 'fa-check' : 'fa-circle')} /> {r.lib}</li>)}
                </ul>
                <div className="form-group"><label htmlFor="mdp-2">Confirmer le mot de passe</label>
                    <ChampSecret id="mdp-2" valeur={conf} onChange={setConf} autoComplete="new-password" /></div>
                <button type="submit" className="btn btn-primary mdp-bouton" disabled={!valide || enCours}>
                    {enCours ? 'Enregistrement…' : invitation ? 'Créer mon mot de passe' : 'Enregistrer le nouveau mot de passe'}
                </button>
                <p className="mdp-pied">Lien valable jusqu'au {dateHeure(vue.expire_le)} · un seul usage.</p>
            </form>
        </Habit>
    );
}
